// @flow
import $ from 'jquery'
import _ from 'lodash'
import React, { Component } from 'react'
import { Table as TableTag, BodyRow, Cell as CellTag, FooterRow } from '../MainTable'
import styled from 'styled-components'
import Value from './Value'
import TableFloatHeader from '../../main/legacy/TableFloatHeader'
import TableFloatFooter from '../../main/legacy/TableFloatFooter'
import { groupHeaders } from '../controls/groupHeaders'
import { translateFunc, translations } from '../../shared/localize'
import type { GroupedHeaders } from '../controls/groupHeaders'
import Thead from './Thead'

const StyledTable = styled(TableTag)`
  margin: 0 0 10px 0;
  border-collapse: separate;
  border-bottom: 0;
  z-index: 1;
`
const Cell = styled(CellTag)`
  font-size: 10px;
  padding: 9px 5px;
  text-align: right;
  font-size: 13px;
  width: initial;
`
export const fixedCellCommonStyle = `
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: white;
`
const FixedCell = styled(Cell)`
  ${fixedCellCommonStyle}
`
const ContentWrapper = styled.div`
  overflow: auto;
  width: 100%;
`
const TableTitle = styled.div`
  margin: 60px 0 20px 0;
`

type StatisticHeaders = {|
  headers: Array<string>,
  goal_names: Array<string>
|}

type Props = {|
  data: Object,
  references: {|
    advertisements: { [string]: { name: string } },
    audiences: { [string]: { name: string } },
    campaigns: { [string]: { name: string } }
  |}
|}

type State = {|
  data: Object,
  sortIndex: number,
  sortDirection: 'asc' | 'desc' | 'none'
|}

const t = translateFunc(translations.statistics)
const headerT = translations.table_headers

export default class Table extends Component<Props, State> {
  wrapper: any
  table: any
  floatHeader: any
  floatFooter: any

  constructor(props: Props) {
    super(props)

    this.state = {
      data: props.data,
      sortIndex: 0,
      sortDirection: 'none'
    }
  }

  render() {
    const { references } = this.props
    const sortedData = this.sortData(this.state.data)
    const { originalIndexesMap, groups: groupedHeaders } = this.groupHeadersWithGoalNames(
      sortedData.headers
    )
    return (
      <div>
        <TableTitle>{sortedData.title}</TableTitle>
        <ContentWrapper
          ref={(el) => {
            this.wrapper = el
          }}
        >
          <StyledTable
            ref={(el) => {
              this.table = el
            }}
          >
            <Thead
              data={sortedData}
              groupedHeaders={groupedHeaders}
              t={t}
              headerColSpan={Math.max(sortedData.sub_headers.length, 1) + 1}
              sortDirection={this.state.sortDirection}
              sortIndex={this.state.sortIndex}
              onColumnClick={(newIndex) => {
                if (this.state.sortDirection === 'none') {
                  this.setState({ sortDirection: 'desc', sortIndex: newIndex })
                } else if (this.state.sortDirection === 'desc') {
                  this.setState({ sortDirection: 'asc', sortIndex: newIndex })
                } else {
                  this.setState({ sortDirection: 'none', sortIndex: 0 })
                }
              }}
            />
            <tbody>
              {_.map(_.filter(sortedData.records, { type: 'row' }), (record, rowIndex: number) => (
                <BodyRow key={rowIndex}>
                  <FixedCell key={-1} className="nowrap">
                    <Value value={record.label} references={references} />
                  </FixedCell>
                  {_.map(originalIndexesMap, (originalIndex: number) => [
                    <Cell key={-1} className="nowrap" />,
                    _.map(record.values[originalIndex], (value, valueIndex: number) => (
                      <Cell key={valueIndex} className="nowrap">
                        <Value value={value} references={references} />
                      </Cell>
                    ))
                  ])}
                </BodyRow>
              ))}
            </tbody>
            <tfoot>
              {_.map(
                _.filter(sortedData.records, { type: 'total' }),
                (record, rowIndex: number) => (
                  <FooterRow key={rowIndex} className="t-FooterRow">
                    <FixedCell key={-1} className="nowrap">
                      <Value value={record.label} references={references} />
                    </FixedCell>
                    {_.map(originalIndexesMap, (originalIndex: number) => [
                      <Cell key={-1} className="nowrap" />,
                      _.map(record.values[originalIndex], (value, valueIndex: number) => (
                        <Cell key={valueIndex} className="nowrap">
                          <Value value={value} references={references} />
                        </Cell>
                      ))
                    ])}
                  </FooterRow>
                )
              )}
            </tfoot>
          </StyledTable>
        </ContentWrapper>
      </div>
    )
  }

  componentDidUpdate() {
    if (!_.isEqual(this.props.data, this.state.data)) {
      this.setState({ data: this.props.data, sortIndex: 0, sortDirection: 'none' })
    }
    this.floatHeader.refresh()
    this.floatFooter.refresh()
  }

  componentDidMount() {
    let $table = $(this.table)
    let $wrapper = $(this.wrapper)

    this.floatHeader = new TableFloatHeader($table, $wrapper)
    this.floatFooter = new TableFloatFooter($table, $wrapper)
  }

  groupHeadersWithGoalNames(
    headers: StatisticHeaders
  ): {|
    originalIndexesMap: Array<number>, // header indexes before grouping
    groups: GroupedHeaders
  |} {
    const groupedHeaders: GroupedHeaders = groupHeaders(headers.headers)
    const translatedGroupedHeaders: GroupedHeaders = (_.fromPairs(
      _.map(groupedHeaders, (groupHeaders, group) => {
        return [group, _.map(groupHeaders, (header) => headerT[header])]
      })
    ): any)
    const oldHeaderOrder = headers.headers
    const newHeaderOrder = _.flatten(_.values(groupedHeaders))
    const originalIndexesMap = _.map(
      newHeaderOrder,
      (newHeader) => _.findIndex(oldHeaderOrder, (header) => header === newHeader) || 0
    )
    // goal names have the same order
    _.map(headers.goal_names, () => originalIndexesMap.push(originalIndexesMap.length))
    return {
      originalIndexesMap,
      groups: {
        ...translatedGroupedHeaders,
        metric: _.concat(translatedGroupedHeaders.metric, headers.goal_names)
      }
    }
  }

  sortData(data: Object) {
    const { sortIndex, sortDirection } = this.state
    if (sortDirection === 'none') return data

    const rows = _.filter(data.records, (row) => {
      return row['type'] === 'row'
    })
    const totalRow = _.last(data.records)

    const direction = sortDirection === 'asc' ? 1 : -1

    const sortedRows = _.sortBy(rows, (row) => {
      return direction * row['values'][sortIndex][0]['value']
    })

    return { ...data, records: _.concat(sortedRows, totalRow) }
  }
}
